import React from 'react';
import { Flex, Skeleton } from 'antd';
import './AuthorizedUsersSkeleton.css';

interface AuthorizedUsersSkeletonProps {
  /** How many placeholder rows to render. */
  rows?: number;
}

/**
 * Loading placeholder for the End User Access list.
 *
 * Mirrors the real row geometry so the table doesn't shift when data lands:
 *  - Username: text line (flex 2)
 *  - Key:      text line (flex 2)
 *  - Status:   dot + label (flex 1)
 *  - Actions:  square icon button (fixed 30px)
 *
 * Follows the same approach as {@link UsersTableSkeleton}: every block gets an
 * explicit `width` + `minWidth`, since antd's Skeleton.Input collapses inside
 * flex containers when only a percentage width is given.
 */
export function AuthorizedUsersSkeleton({ rows = 5 }: AuthorizedUsersSkeletonProps) {
  return (
    <div className="authorized-users-skeleton" aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={`user-skeleton-${i}`} className="authorized-users-skeleton__row">
          <Flex align="center" gap={20}>
            <div className="authorized-users-skeleton__col authorized-users-skeleton__col--wide">
              <Skeleton.Input
                active
                size="small"
                style={{ width: 160, minWidth: 160, height: 16 }}
              />
            </div>

            <div className="authorized-users-skeleton__col authorized-users-skeleton__col--wide">
              <Skeleton.Input
                active
                size="small"
                style={{ width: 130, minWidth: 130, height: 16 }}
              />
            </div>

            <div className="authorized-users-skeleton__col authorized-users-skeleton__col--status">
              <Flex align="center" gap={8}>
                <Skeleton.Avatar active size={10} shape="circle" />
                <Skeleton.Input
                  active
                  size="small"
                  style={{ width: 54, minWidth: 54, height: 16 }}
                />
              </Flex>
            </div>

            <div className="authorized-users-skeleton__col authorized-users-skeleton__col--actions">
              <Skeleton.Avatar active size={20} shape="square" />
            </div>
          </Flex>
        </div>
      ))}
    </div>
  );
}
